import * as React from 'react';
import { css } from 'aphrodite';
import { Button } from '../../../components-v2/Button';
import { WarningIcon } from '../../../components/svgs/icons/WarningIcon';
import { styleSheet as styles } from './styles/AutomationFlowErrorBoundaryStyles';

interface Props {
	children: React.ReactNode;
	onReset?: () => void;
	onError?: (error: Error) => void;
}

interface State {
	hasError: boolean;
}

/**
 * Error boundary for automation flow rendering.
 * Uses the same generic error experience as the rest of the app.
 */
export class AutomationFlowErrorBoundary extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
			hasError: false,
		};
	}

	static getDerivedStateFromError(): Partial<State> {
		return {
			hasError: true,
		};
	}

	componentDidCatch(err: Error): void {
		console.error('[AutomationFlowErrorBoundary] Caught error:', err);
		this.props.onError?.(err);
	}

	handleReset = (): void => {
		this.setState({ hasError: false });
		this.props.onReset?.();
	};

	render(): React.ReactNode {
		if (this.state.hasError) {
			return (
				<div className={css(styles.container)} role='alert' aria-live='assertive'>
					<div className={css(styles.content)}>
						<WarningIcon className={css(styles.icon)} />
						<h2 className={css(styles.title)}>An error occurred</h2>
						<p className={css(styles.message)}>We couldn&apos;t display this automation. Please try again.</p>
						<Button variant='primary' onClick={this.handleReset}>
							Retry
						</Button>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}
